(() => {
  if (window.CurlRebuyBundleAdapter) return;

  const clean = value => String(value || '').replace(/\s+/g, ' ').trim();
  const normalized = value => clean(value).toLocaleLowerCase();
  const numberFrom = value => {
    const match = clean(value).match(/\d+/);
    return match ? Number(match[0]) : null;
  };
  const moneyText = node => {
    if (!node) return '';
    const copy = node.cloneNode(true);
    copy.querySelectorAll('.sr-only, .visually-hidden, .screen-reader-text').forEach(item => item.remove());
    return clean(copy.textContent);
  };
  const isDisabled = node => !node || node.disabled || node.getAttribute('aria-disabled') === 'true';

  class CurlRebuyBundleAdapter {
    constructor(root) {
      if (!(root instanceof Element)) throw new Error('A Rebuy bundle widget element is required.');
      this.root = root;
      this.listeners = new Set();
      this.pending = new Set();
      this.destroyed = false;
      this.operation = false;
      this.submitting = false;
      this.sawSubmissionBusy = false;
      this.submissionCount = null;
      this.submissionTimer = null;
      this.scheduled = false;
      this.signature = '';
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(root, { subtree: true, childList: true, characterData: true, attributes: true });
      this.onChange = () => this.schedule();
      root.addEventListener('change', this.onChange);
      root.addEventListener('input', this.onChange);
      this.signature = JSON.stringify(this.read());
    }

    cta() {
      return this.root.querySelector('.rebuy-bundle-builder__cta-container > button.rebuy-button');
    }

    appBusy() {
      const button = this.cta();
      return !!button && (button.getAttribute('aria-busy') === 'true' || button.matches('.loading, .is-loading, .is-busy, .busy') || !!button.querySelector('.loading, .is-loading, [aria-busy="true"]'));
    }

    catalog() {
      return Array.from(this.root.querySelectorAll('.rebuy-product-block')).map(node => {
        const idClass = Array.from(node.classList).find(value => /^product-id-\d+$/.test(value));
        if (!idClass) return null;
        const link = node.querySelector('.rebuy-product-title a');
        let url;
        try {
          url = new URL(link ? link.getAttribute('href') : '', window.location.origin);
        } catch (_) {
          url = new URL(window.location.href);
        }
        const select = node.querySelector('.rebuy-product-options select');
        const variantId = clean(select ? select.value : url.searchParams.get('variant'));
        const variants = select ? Array.from(select.options).filter(option => option.value).map(option => ({
          id: String(option.value),
          title: clean(option.textContent),
          available: !option.disabled
        })) : [{ id: variantId, title: 'Default Title', available: true }];
        return {
          node,
          select,
          id: idClass.replace('product-id-', ''),
          handle: (url.pathname.match(/\/products\/([^/]+)/) || [])[1] || '',
          title: clean(link ? link.textContent : node.querySelector('.rebuy-product-title')?.textContent),
          variantId,
          variants,
          priceText: moneyText(node.querySelector('.rebuy-product-price .rebuy-money.sale') || node.querySelector('.rebuy-product-price .rebuy-money:not(.compare-at)'))
        };
      }).filter(Boolean);
    }

    selected(catalog) {
      return Array.from(this.root.querySelectorAll('.rebuy-bundle-builder__product-holder-data')).map((node, index) => {
        const title = clean(node.querySelector('.rebuy-bundle-builder__product-holder-info-title')?.textContent);
        const variantTitle = clean(node.querySelector('.rebuy-bundle-builder__product-holder-info-variant')?.textContent);
        const titleLink = node.querySelector('a[href*="/products/"]');
        const handle = titleLink ? (titleLink.getAttribute('href').match(/\/products\/([^/?#]+)/) || [])[1] : '';
        const product = catalog.find(item => handle && item.handle === handle) || catalog.find(item => normalized(item.title) === normalized(title));
        const shade = normalized(variantTitle);
        const variant = product?.variants.find(item => normalized(item.title) === shade) || product?.variants.find(item => {
          const option = normalized(item.title);
          return shade && option && (shade.endsWith(': ' + option) || shade.endsWith(' / ' + option));
        }) || (product?.variants.length === 1 ? product.variants[0] : null);
        const image = node.querySelector('.rebuy-product-media img');
        return {
          index,
          productId: product?.id || '',
          handle: product?.handle || handle || '',
          variantId: variant?.id || '',
          title,
          variantTitle,
          image: image ? image.currentSrc || image.getAttribute('src') || image.getAttribute('data-src') || '' : '',
          priceText: moneyText(node.querySelector('.rebuy-product-price .rebuy-money.sale') || node.querySelector('.rebuy-product-price .rebuy-money:not(.compare-at)'))
        };
      });
    }

    read() {
      const catalog = this.catalog();
      const lines = this.selected(catalog);
      const count = numberFrom(this.root.querySelector('.rebuy-bundle-builder__group-container-header-products_count')?.textContent) ?? lines.length;
      const max = numberFrom(this.root.querySelector('.rebuy-bundle-builder__group-container-header-container-length')?.textContent);
      const cta = this.cta();
      const busy = this.operation || this.submitting || this.appBusy();
      const totals = this.root.querySelector('.rebuy-bundle-builder__cta-container');
      const tiers = Array.from(this.root.querySelectorAll('.rebuy-bundle-builder__progress-step')).map(node => {
        const label = clean(node.querySelector('.rebuy-bundle-builder__progress-step-label')?.textContent);
        const threshold = numberFrom(label);
        return {
          count: threshold,
          label,
          value: clean(node.querySelector('.rebuy-bundle-builder__progress-step-value')?.textContent).replace(/^save\s+/i, ''),
          complete: threshold !== null ? count >= threshold : node.matches('.complete, .completed, .is-complete')
        };
      });
      const progressNode = this.root.querySelector('[role="progressbar"]');
      const progressValue = Number(progressNode?.getAttribute('aria-valuenow'));
      const errors = Array.from(this.root.querySelectorAll('.rebuy-error, .rebuy-errors, .rebuy-bundle-builder__error, [role="alert"]')).filter(node => !node.hidden && node.getAttribute('aria-hidden') !== 'true').map(node => clean(node.textContent)).filter(Boolean);
      return {
        count,
        max,
        canSubmit: !isDisabled(cta) && !busy,
        busy,
        totalText: moneyText(totals?.querySelector('.rebuy-money.sale') || totals?.querySelector('.rebuy-money:not(.compare-at)')),
        subtotalText: moneyText(totals?.querySelector('.rebuy-money.compare-at') || totals?.querySelector('.rebuy-money.sale') || totals?.querySelector('.rebuy-money:not(.compare-at)')),
        tiers,
        progress: progressNode && Number.isFinite(progressValue) ? progressValue : 0,
        products: catalog.map(product => {
          const controls = this.controls(product.node);
          const selectedVariant = product.variants.find(item => item.id === product.variantId);
          return {
            id: product.id,
            handle: product.handle,
            title: product.title,
            variantId: product.variantId,
            variants: product.variants,
            quantity: lines.filter(line => line.productId === product.id && (!line.variantId || line.variantId === product.variantId)).length,
            canAdd: !busy && (max === null || count < max) && selectedVariant?.available !== false && !isDisabled(controls.increment),
            priceText: product.priceText
          };
        }),
        lines,
        errors: Array.from(new Set(errors))
      };
    }

    controls(node) {
      const area = node.querySelector('.rebuy-bundle-builder__product-quantity');
      const quantity = area?.querySelector('.rebuy-bundle-builder__item-quantity-widget');
      const buttons = quantity ? Array.from(quantity.querySelectorAll('button')) : [];
      return {
        decrement: buttons[0] || null,
        increment: buttons[1] || area?.querySelector('button.rebuy-button') || null
      };
    }

    schedule() {
      if (this.destroyed || this.scheduled) return;
      this.scheduled = true;
      queueMicrotask(() => {
        this.scheduled = false;
        if (this.destroyed) return;
        if (this.submitting) {
          const busy = this.appBusy();
          this.sawSubmissionBusy ||= busy;
          const count = numberFrom(this.root.querySelector('.rebuy-bundle-builder__group-container-header-products_count')?.textContent);
          if ((this.sawSubmissionBusy && !busy) || (count !== null && count !== this.submissionCount)) this.releaseSubmission();
        }
        const state = this.read();
        this.pending.forEach(check => check(state));
        const signature = JSON.stringify(state);
        if (signature === this.signature) return;
        this.signature = signature;
        this.listeners.forEach(callback => callback(state));
      });
    }

    subscribe(callback) {
      if (typeof callback !== 'function') throw new Error('A subscription callback is required.');
      this.listeners.add(callback);
      return () => this.listeners.delete(callback);
    }

    waitFor(predicate, message, timeout = 5000) {
      if (this.destroyed) return Promise.reject(new Error('The bundle adapter has been destroyed.'));
      return new Promise((resolve, reject) => {
        let timer;
        const check = state => {
          if (!predicate(state)) return;
          clearTimeout(timer);
          this.pending.delete(check);
          resolve(state);
        };
        check.cancel = () => {
          clearTimeout(timer);
          this.pending.delete(check);
          reject(new Error('The bundle widget was disconnected.'));
        };
        this.pending.add(check);
        timer = setTimeout(() => {
          this.pending.delete(check);
          reject(new Error(message));
        }, timeout);
        check(this.read());
      });
    }

    async selectVariant(productId, variantId) {
      let product = this.catalog().find(item => item.id === String(productId));
      if (!product) throw new Error('This product is not available in the bundle app.');
      const target = String(variantId || product.variantId);
      const variant = product.variants.find(item => item.id === target);
      if (!variant || !variant.available) throw new Error('This product option is unavailable.');
      if (product.select && product.variantId !== target) {
        const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value').set;
        setter.call(product.select, target);
        product.select.dispatchEvent(new Event('change', { bubbles: true }));
        await this.waitFor(() => {
          const fresh = this.catalog().find(item => item.id === String(productId));
          if (!fresh || fresh.variantId !== target) return false;
          const link = fresh.node.querySelector('.rebuy-product-title a');
          const href = link?.getAttribute('href') || '';
          try {
            const linkVariant = new URL(href, window.location.origin).searchParams.get('variant');
            return !linkVariant || linkVariant === target;
          } catch (_) {
            return false;
          }
        }, 'The bundle app did not confirm this product option.');
        await new Promise(resolve => setTimeout(resolve, 0));
      }
      product = this.catalog().find(item => item.id === String(productId));
      if (!product || product.variantId !== target) throw new Error('The selected bundle option changed. Please try again.');
      return product;
    }

    async perform(action) {
      if (this.destroyed) throw new Error('The bundle widget is unavailable.');
      if (this.operation || this.submitting || this.appBusy()) throw new Error('Please wait for the bundle app to finish.');
      this.operation = true;
      this.schedule();
      try {
        await action();
      } finally {
        this.operation = false;
        this.schedule();
      }
      return this.read();
    }

    add(productId, variantId) {
      return this.perform(async () => {
        let state = this.read();
        if (state.max !== null && state.count >= state.max) throw new Error('The bundle has reached its item limit.');
        const product = await this.selectVariant(productId, variantId);
        state = this.read();
        if (state.max !== null && state.count >= state.max) throw new Error('The bundle has reached its item limit.');
        const button = this.controls(product.node).increment;
        if (isDisabled(button)) throw new Error('The bundle app cannot add this product option.');
        const count = state.count;
        button.click();
        await this.waitFor(next => next.count > count, 'The bundle app did not add this item. Please try again.');
      });
    }

    remove(index) {
      return this.perform(() => this.removeLine(index));
    }

    async removeLine(index) {
      const state = this.read();
      const line = this.root.querySelectorAll('.rebuy-bundle-builder__product-holder-data')[Number(index)];
      const button = line?.querySelector('.rebuy-bundle-builder__product-holder-info-delete');
      if (isDisabled(button)) throw new Error('This bundle item is no longer available to remove.');
      button.click();
      await this.waitFor(next => next.count < state.count, 'The bundle app did not remove this item. Please try again.');
    }

    decrement(productId, variantId) {
      return this.perform(async () => {
        const product = await this.selectVariant(productId, variantId);
        const state = this.read();
        const button = this.controls(product.node).decrement;
        if (isDisabled(button)) throw new Error('The bundle app cannot decrease this item.');
        button.click();
        await this.waitFor(next => next.count < state.count, 'The bundle app did not decrease this item. Please try again.');
      });
    }

    clear() {
      return this.perform(async () => {
        let state = this.read();
        while (state.count > 0 && state.lines.length > 0) {
          await this.removeLine(state.lines.length - 1);
          state = this.read();
        }
        if (state.count > 0) throw new Error('The bundle app could not clear all selected items.');
      });
    }

    submit() {
      const state = this.read();
      if (this.destroyed || !state.canSubmit) return Promise.reject(new Error('The bundle is not ready to add to cart.'));
      this.submitting = true;
      this.sawSubmissionBusy = false;
      this.submissionCount = state.count;
      this.submissionTimer = setTimeout(() => {
        this.releaseSubmission();
        this.schedule();
      }, 15000);
      this.schedule();
      this.cta().click();
      return Promise.resolve(this.read());
    }

    releaseSubmission() {
      this.submitting = false;
      clearTimeout(this.submissionTimer);
      this.submissionTimer = null;
    }

    destroy() {
      this.destroyed = true;
      this.observer.disconnect();
      this.root.removeEventListener('change', this.onChange);
      this.root.removeEventListener('input', this.onChange);
      this.releaseSubmission();
      this.pending.forEach(check => check.cancel());
      this.pending.clear();
      this.listeners.clear();
    }
  }

  window.CurlRebuyBundleAdapter = CurlRebuyBundleAdapter;
})();
